import React, { forwardRef, useImperativeHandle, useRef, useMemo } from "react";
import { COLORS } from "../config/appConfig";

/**
 * Renders the caption using the rich `tokens` array from data.json.
 *
 * Each token: { id, text, grounded, category, value, node_id, source_type, confidence }
 * Segments map to tokens via `segment.token_ids` (matching token.id).
 *
 * Props:
 *   tokens    — the rich tokens array from currentImage.tokens
 *   entities  — segments array (each with id, token_ids, mask, label, explanation)
 *   hoveredId — currently hovered segment id
 *   onCaptionHover — callback(segmentId | null)
 *   onCaptionClick — callback({ segmentId, tokenId }) for persistent selection
 *   onWordRef — callback for boundary capture
 */
const CaptionText = forwardRef(
  ({
    tokens,
    entities,
    hoveredId,
    hoveredTokenId,
    onCaptionHover,
    onCaptionClick,
    onWordRef,
    requireSegmentForInteraction = false,
  }, ref) => {
    const containerRef = useRef(null);
    const wordRefs = useRef(new Map()); // tokenId -> DOM element

    // Build lookup: token id → segment id
    const tokenToSegment = useMemo(() => {
      const map = {};
      (entities || []).forEach((segment) => {
        (segment.token_ids || []).forEach((tid) => {
          map[tid] = segment.id;
        });
      });
      return map;
    }, [entities]);

    // Build lookup: segment id → color index
    const segmentIndexMap = useMemo(() => {
      const map = {};
      (entities || []).forEach((segment, index) => {
        map[segment.id] = index;
      });
      return map;
    }, [entities]);

    // Expose imperative API to parent for boundary capture
    useImperativeHandle(
      ref,
      () => ({
        getBoundingClientRect: () =>
          containerRef.current?.getBoundingClientRect(),
        getSubComponentBoundaries: () => {
          const boundaries = {};
          wordRefs.current.forEach((el, tokenId) => {
            if (el) {
              const rect = el.getBoundingClientRect();
              boundaries[`captionText_token_${tokenId}`] = {
                x: rect.x,
                y: rect.y,
                width: rect.width,
                height: rect.height,
                top: rect.top,
                left: rect.left,
                right: rect.right,
                bottom: rect.bottom,
                segmentId: tokenToSegment[tokenId] ?? null,
              };
            }
          });
          return boundaries;
        },
      }),
      [tokenToSegment]
    );

    const safeTokens = tokens || [];

    return (
      <div ref={containerRef} className="caption-container">
        {safeTokens.map((token) => {
          const segId = tokenToSegment[token.id];
          const isEntity = segId !== undefined;
          const isInteractive = requireSegmentForInteraction
            ? segId !== undefined
            : segId !== undefined || !!token.grounded;
          const isHovered =
            (segId !== undefined && hoveredId === segId) || hoveredTokenId === token.id;
          const colorIndex = isEntity ? (segmentIndexMap[segId] || 0) : 0;
          const hoverStyle = isHovered
            ? {
                backgroundColor: isEntity ? COLORS[colorIndex % 4] : "#0f766e",
                color: "#fff",
                boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.08)",
                filter: "saturate(0.92)",
              }
            : undefined;

          return (
            <span
              key={token.id}
              ref={(el) => {
                wordRefs.current.set(token.id, el || null);
                onWordRef?.(token.id, el, { segmentId: segId ?? null });
              }}
              data-token-id={token.id}
              data-segment-id={segId || undefined}
              className={isInteractive ? "highlight-word" : "word"}
              onMouseEnter={
                isInteractive
                  ? () => onCaptionHover?.({ segmentId: segId ?? null, tokenId: token.id, text: token.text })
                  : undefined
              }
              onMouseLeave={isInteractive ? () => onCaptionHover?.(null) : undefined}
              onClick={
                isInteractive
                  ? () => onCaptionClick?.({ segmentId: segId ?? null, tokenId: token.id, text: token.text })
                  : undefined
              }
              onKeyDown={
                isInteractive
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onCaptionClick?.({ segmentId: segId ?? null, tokenId: token.id, text: token.text });
                      }
                    }
                  : undefined
              }
              role={isInteractive && onCaptionClick ? "button" : undefined}
              tabIndex={isInteractive && onCaptionClick ? 0 : undefined}
              style={hoverStyle}
            >
              {token.text}
            </span>
          );
        })}
      </div>
    );
  }
);

CaptionText.displayName = "CaptionText";

export default CaptionText;
